/**
 * 파일 보관함 좌측 카테고리 레일.
 *
 * 가로 Tabs 대신 세로 사이드바로 카테고리를 노출 — 각 항목은 아이콘 + 한글명 +
 * 한 줄 설명을 함께 보여줘서 처음 들어온 사용자도 카테고리 의미를 즉시 파악 가능.
 */

import React from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import type { FileCategory } from '../../models/FileStorage';
import { CATEGORY_META } from './categoryMeta';

interface CategoryRailProps {
  /** 현재 선택된 카테고리. */
  active: FileCategory;
  /** 카테고리 변경 콜백. */
  onChange: (next: FileCategory) => void;
}

const CategoryRail: React.FC<CategoryRailProps> = ({ active, onChange }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      role="navigation"
      aria-label="파일 카테고리"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1.5,
        borderRadius: 2,
        bgcolor: isDark ? alpha('#1a1a1a', 0.65) : alpha('#ffffff', 0.92),
        border: '1px solid',
        borderColor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06),
        position: 'sticky',
        top: 16,
      }}
    >
      <Typography
        variant="overline"
        sx={{
          px: 1,
          color: 'text.secondary',
          letterSpacing: 0.6,
          fontWeight: 600,
        }}
      >
        파일 카테고리
      </Typography>

      {CATEGORY_META.map(({ id, label, code, description, Icon, accent }) => {
        const isActive = id === active;
        return (
          <Box
            key={id}
            component="button"
            type="button"
            onClick={() => onChange(id)}
            aria-pressed={isActive}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              px: 1.5,
              py: 1.25,
              border: 'none',
              borderRadius: 1.5,
              textAlign: 'left',
              cursor: 'pointer',
              position: 'relative',
              transition: 'background 160ms, transform 160ms',
              bgcolor: isActive
                ? isDark
                  ? alpha(accent, 0.18)
                  : alpha(accent, 0.12)
                : 'transparent',
              outline: isActive ? `1px solid ${alpha(accent, 0.45)}` : '1px solid transparent',
              '&:hover': {
                bgcolor: isActive
                  ? isDark
                    ? alpha(accent, 0.24)
                    : alpha(accent, 0.18)
                  : isDark
                    ? alpha('#fff', 0.04)
                    : alpha('#000', 0.04),
              },
              '&::before': isActive
                ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 6,
                    bottom: 6,
                    width: 3,
                    borderRadius: 2,
                    bgcolor: accent,
                  }
                : undefined,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isActive
                  ? accent
                  : isDark
                    ? alpha(accent, 0.18)
                    : alpha(accent, 0.12),
                color: isActive ? '#fff' : accent,
                transition: 'background 160ms, color 160ms',
              }}
            >
              <Icon fontSize="small" />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: isActive ? 700 : 600,
                    color: isActive ? 'text.primary' : 'text.primary',
                    lineHeight: 1.2,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    letterSpacing: 0.4,
                  }}
                >
                  {code}
                </Typography>
              </Box>
              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: '0.72rem',
                  color: 'text.secondary',
                  lineHeight: 1.35,
                  whiteSpace: 'normal',
                }}
              >
                {description}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default CategoryRail;
